import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Checkbox, Skeleton, Tooltip } from '@mui/material';
import * as S from '../../styles';
import { COLORS } from '../../styles/constants';
import {
  EDITABLE_COLUMNS,
  SHIPMENT_PLAN_EDITABLE_COLUMNS,
  COLUMN_TO_FIELD_MAP,
  ERRORS,
  BUTTONS,
  OVERLAYS,
  FORWARDER_ONLY_COLUMNS,
  TRANSPORT_ONLY_COLUMNS,
  SHIPPING_LINE_TRANSPORT_FORWARDER_COLUMNS,
} from '../../TransportCloudTable.consts';
import { Item } from '../../TransportCloudTable.types';
import { getEditComponent } from '../../utils/editComponentMap';
import { EditableCell } from '../EditableCell/EditableCell';
import { StatusChip } from '../StatusChip';

const USER_COLORS = [
  '#FF5252',
  '#4CAF50',
  '#2196F3',
  '#FF9800',
  '#9C27B0',
  '#00BCD4',
  '#FFEB3B',
  '#E91E63',
  '#8BC34A',
  '#FFC107',
];

const FIXED_WIDTH_EDIT_COLUMNS = new Set(['shippingLineHaulage']);

const CHECK_CELL_SX = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  minHeight: '24px',
} as const;

const CHECK_ICON_SX = { fontSize: '0.9rem' } as const;

const getColorForUser = (username: string): string => {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % USER_COLORS.length;
  return USER_COLORS[index];
};

const formatUserDisplay = (username: string) => {
  if (username.length === 3 && username === username.toUpperCase()) {
    return username;
  }
  return username.charAt(0).toUpperCase();
};

const formatCellDisplay = (value: any, columnKey: string): any => {
  if (
    columnKey === 'truckerRate' &&
    typeof value === 'object' &&
    value !== null
  ) {
    const amount = value.rate || value.amount || '';
    let currencyCode = '';
    if (typeof value.currency === 'string') {
      currencyCode = value.currency;
    } else if (typeof value.currency === 'object' && value.currency?.currency) {
      currencyCode = value.currency.currency;
    }
    return currencyCode ? `${amount} ${currencyCode}` : amount;
  } else if (columnKey === 'shippingLineHaulage') {
    return value === true ? (
      <Box sx={CHECK_CELL_SX}>
        <CheckIcon sx={CHECK_ICON_SX} />
      </Box>
    ) : (
      ''
    );
  } else if (columnKey === 'containerStatus') {
    if (!value) return '';
    const { name, color } = value as { name: string; color: string | null };
    return <StatusChip name={name} color={color} />;
  }
  return value;
};

type ColumnPrivilegeContext = {
  canEditAllColumns: boolean;
  hasForwarderEditPrivilege: boolean;
  hasTransportEditPrivilege: boolean;
  isshippingLineHaulage: boolean;
};

const canEditColumn = (
  columnKey: string,
  {
    canEditAllColumns,
    hasForwarderEditPrivilege,
    hasTransportEditPrivilege,
    isshippingLineHaulage,
  }: ColumnPrivilegeContext,
): boolean => {
  if (canEditAllColumns) return true;
  if (!hasForwarderEditPrivilege && !hasTransportEditPrivilege) return false;

  if (isshippingLineHaulage && !hasForwarderEditPrivilege) return false;

  if (FORWARDER_ONLY_COLUMNS.has(columnKey)) return hasForwarderEditPrivilege;

  if (TRANSPORT_ONLY_COLUMNS.has(columnKey)) {
    return (
      hasTransportEditPrivilege ||
      (isshippingLineHaulage &&
        hasForwarderEditPrivilege &&
        SHIPPING_LINE_TRANSPORT_FORWARDER_COLUMNS.has(columnKey))
    );
  }

  return true;
};

const TOOLTIP_SX = { fontSize: '0.7rem' } as const;
const TOOLTIP_DISABLED_PROPS = {
  tooltip: { sx: TOOLTIP_SX },
} as const;

type SavingCellInfo = {
  itemId: string;
  columnKey: string;
  status:
    | 'loading'
    | 'success'
    | 'error'
    | 'fading'
    | 'success-other'
    | 'fading-other';
  oldValue?: any;
};

type LockedCellInfo = {
  rowId: number;
  fieldName: string;
  user: string;
};

interface TableRowItemProps {
  item: Item;
  itemId: string;
  groupDate: string;
  itemIndex: number;
  loading: boolean;
  filteredHeaders: [string, string][];
  hidingColumn: string | null;
  hidingColumns: Set<string>;
  showingColumns: Set<string>;
  editingCell: { itemId: string; columnKey: string } | null;
  editingValue: any;
  rowSavingCells: SavingCellInfo[] | undefined;
  validationError: {
    itemId: string;
    columnKey: string;
    errorMessage: string;
    status?: 'active' | 'fading';
  } | null;
  rowLockedCells: LockedCellInfo[] | undefined;
  currentUser: string;
  canEditAllColumns: boolean;
  hasForwarderEditPrivilege: boolean;
  hasTransportEditPrivilege: boolean;
  canEditAllRecords: boolean;
  hasOwnerEditPrivilege: boolean;
  canEditAllShipmentPlans: boolean;
  hasOwnerEditShipmentPlansPrivilege: boolean;
  ownerIds: Set<number>;
  canAddAllLoads: boolean;
  hasOwnerAddLoadPrivilege: boolean;
  isSelected: boolean;
  isDateChanged: boolean;
  dateChangeInfo:
    | { newDate: string; status: 'pending' | 'accepted' }
    | undefined
    | null;
  isDeleted: boolean;
  deletionStatus: 'loading' | 'success' | 'success-other' | 'error' | null;
  onCellEdit: (itemId: string, columnKey: string, cellValue: any) => void;
  onCellSave: (
    itemId: string,
    columnKey: string,
    newValue: any,
    skipDriverCheck?: boolean,
  ) => void;
  onCellCancel: () => void;
  onEditValueChange: (value: any) => void;
  onCheckboxClick: (itemId: string) => void;
  onReload: () => void;
  onAcceptDateChange: (itemId: string) => void;
}

const TableRowItemInner: React.FC<TableRowItemProps> = ({
  item,
  itemId,
  groupDate,
  itemIndex,
  loading,
  filteredHeaders,
  hidingColumn,
  hidingColumns,
  showingColumns,
  editingCell,
  editingValue,
  rowSavingCells,
  validationError,
  rowLockedCells,
  currentUser,
  canEditAllColumns,
  hasForwarderEditPrivilege,
  hasTransportEditPrivilege,
  canEditAllRecords,
  hasOwnerEditPrivilege,
  canEditAllShipmentPlans,
  hasOwnerEditShipmentPlansPrivilege,
  ownerIds,
  canAddAllLoads,
  hasOwnerAddLoadPrivilege,
  isSelected,
  isDateChanged,
  dateChangeInfo,
  isDeleted,
  deletionStatus,
  onCellEdit,
  onCellSave,
  onCellCancel,
  onEditValueChange,
  onCheckboxClick,
  onReload,
  onAcceptDateChange,
}) => {
  const isShipmentPlanRecord =
    item.jobEntryId == null && item.shipmentPlanContainerId != null;
  const isRecordAccessible = isShipmentPlanRecord
    ? canEditAllShipmentPlans ||
      (hasOwnerEditShipmentPlansPrivilege &&
        item.ownerId != null &&
        ownerIds.has(item.ownerId))
    : canEditAllRecords ||
      (hasOwnerEditPrivilege &&
        item.ownerId != null &&
        ownerIds.has(item.ownerId));
  const isEditable =
    item.jobEntryId != null || item.shipmentPlanContainerId != null;
  const isDisabledByRecordPrivilege = isEditable && !isRecordAccessible;
  const isDeleting = deletionStatus === 'loading';
  const isshippingLineHaulage = item.shippingLineHaulage === true;

  const columnPrivilegeContext: ColumnPrivilegeContext = {
    canEditAllColumns,
    hasForwarderEditPrivilege,
    hasTransportEditPrivilege,
    isshippingLineHaulage,
  };

  return (
    <S.StyledTableRow
      key={`${groupDate}-${item.jobEntryId || `item-${itemIndex}`}`}
      $isBlocked={false}
      $hasDateChanged={isDateChanged}
      $dateChangeAccepted={dateChangeInfo?.status === 'accepted'}
      $isDeleted={isDeleted}
      $isSelected={isSelected}
      $isshippingLineHaulage={isshippingLineHaulage}
      data-selected={isSelected}
      data-date-changed={isDateChanged}
      data-date-change-accepted={dateChangeInfo?.status === 'accepted'}
      data-deleted={isDeleted}
      data-shipping-line-transport={isshippingLineHaulage}
    >
      <S.StickyCheckboxCell $deletionStatus={deletionStatus}>
        {loading ? (
          <Skeleton variant='circular' width='15px' height='15px' />
        ) : (
          <Checkbox
            color='primary'
            size='small'
            checked={isSelected}
            disabled={isDeleting}
            onClick={(e) => {
              e.stopPropagation();
              onCheckboxClick(itemId);
            }}
            inputProps={{
              'aria-label': `Select record ${itemId}`,
            }}
          />
        )}
      </S.StickyCheckboxCell>
      {filteredHeaders.map(([key], index) => {
        const cellValue = item[key as keyof Item];
        const isCurrentlyEditing = editingCell?.columnKey === key;
        const currentSavingCell = rowSavingCells?.find(
          (cell) => cell.columnKey === key,
        );
        const isCurrentlySaving = !!currentSavingCell;
        const hasValidationError = validationError?.columnKey === key;

        const fieldName = COLUMN_TO_FIELD_MAP[key] || key;
        const lockedByOther = rowLockedCells?.find(
          (cell) => cell.fieldName === fieldName && cell.user !== currentUser,
        );
        const isCellLocked = !!lockedByOther && !isCurrentlyEditing;

        const isDisabledByCaptured =
          isEditable &&
          isRecordAccessible &&
          item.captured &&
          (key === 'pickupLocation' || key === 'pickupDate');

        const isDisabledByImport =
          isEditable &&
          isRecordAccessible &&
          !isShipmentPlanRecord &&
          item.tradeType === 'IMPORT' &&
          key === 'weighing';

        const isDisabledByExport =
          isEditable &&
          isRecordAccessible &&
          item.tradeType === 'EXPORT' &&
          key === 'pinCode';

        const isEditableColumn = isShipmentPlanRecord
          ? SHIPMENT_PLAN_EDITABLE_COLUMNS.includes(key)
          : EDITABLE_COLUMNS.includes(key);

        const isDisabledByPrivilege =
          isEditable &&
          isRecordAccessible &&
          isEditableColumn &&
          !canEditColumn(key, columnPrivilegeContext);

        const isCellEditable =
          isEditable &&
          isRecordAccessible &&
          isEditableColumn &&
          !isCellLocked &&
          !isDisabledByCaptured &&
          !isDisabledByImport &&
          !isDeleting &&
          !isDisabledByExport &&
          !isDisabledByPrivilege;

        return (
          <S.HoverableStyledTableCell
            key={key}
            data-item-id={itemId}
            data-column={key}
            data-column-index={index + 1}
            data-column-hiding={
              hidingColumn === key || hidingColumns.has(key) ? '' : undefined
            }
            data-column-showing={showingColumns.has(key) ? '' : undefined}
            data-editing={isCurrentlyEditing ? '' : undefined}
            data-editable={isCellEditable ? '' : undefined}
            data-locked={isCellLocked ? '' : undefined}
            data-saving-status={
              isCurrentlySaving
                ? currentSavingCell?.status || undefined
                : undefined
            }
            data-validation-error={hasValidationError ? '' : undefined}
            data-validation-status={
              hasValidationError
                ? validationError?.status || undefined
                : undefined
            }
            onDoubleClick={
              isCellEditable
                ? () => onCellEdit(itemId, key, cellValue)
                : undefined
            }
          >
            <S.CellContentWrapper>
              {loading ? (
                <Skeleton />
              ) : isCellEditable ? (
                <EditableCell
                  value={cellValue}
                  isEditing={isCurrentlyEditing}
                  maintainExpandedWidth={
                    isCurrentlyEditing &&
                    !isCurrentlySaving &&
                    !FIXED_WIDTH_EDIT_COLUMNS.has(key)
                  }
                  hasValidationError={hasValidationError}
                  validationMessage={
                    hasValidationError
                      ? validationError?.errorMessage
                      : undefined
                  }
                  validationStatus={
                    hasValidationError ? validationError?.status || null : null
                  }
                  onSave={(newValue) => onCellSave(itemId, key, newValue)}
                  onCancel={onCellCancel}
                  editComponent={
                    isCurrentlyEditing
                      ? getEditComponent(key, {
                          value: editingValue,
                          onChange: onEditValueChange,
                          onSave: (newValue, skipDriverCheck) =>
                            onCellSave(itemId, key, newValue, skipDriverCheck),
                          onCancel: onCellCancel,
                          onRowFieldSave: (colKey, val, skipDriverCheck) =>
                            onCellSave(itemId, colKey, val, skipDriverCheck),
                          customerId: item.customerId || null,
                          rowData: item,
                          hasValidationError,
                        })
                      : null
                  }
                >
                  {formatCellDisplay(cellValue, key)}
                </EditableCell>
              ) : (
                formatCellDisplay(cellValue, key)
              )}
            </S.CellContentWrapper>
            {(isCellEditable ||
              isDisabledByCaptured ||
              isDisabledByImport ||
              isDisabledByExport ||
              isDisabledByPrivilege ||
              (isDisabledByRecordPrivilege && isEditableColumn)) &&
              (isDisabledByCaptured ? (
                <Tooltip
                  title={ERRORS.capturedEditDisabled}
                  arrow
                  placement='top'
                  componentsProps={TOOLTIP_DISABLED_PROPS}
                >
                  <S.EditIcon
                    $isDisabled={true}
                    data-disabled='true'
                    data-edit-icon
                  >
                    <DriveFileRenameOutlineIcon />
                  </S.EditIcon>
                </Tooltip>
              ) : isDisabledByImport ? (
                <Tooltip
                  title={ERRORS.importEditDisabled}
                  arrow
                  placement='top'
                  componentsProps={TOOLTIP_DISABLED_PROPS}
                >
                  <S.EditIcon
                    $isDisabled={true}
                    data-disabled='true'
                    data-edit-icon
                  >
                    <DriveFileRenameOutlineIcon />
                  </S.EditIcon>
                </Tooltip>
              ) : isDisabledByExport ? (
                <Tooltip
                  title={ERRORS.exportEditDisabled}
                  arrow
                  placement='top'
                  componentsProps={TOOLTIP_DISABLED_PROPS}
                >
                  <S.EditIcon
                    $isDisabled={true}
                    data-disabled='true'
                    data-edit-icon
                  >
                    <DriveFileRenameOutlineIcon />
                  </S.EditIcon>
                </Tooltip>
              ) : isDisabledByPrivilege ? (
                <Tooltip
                  title={ERRORS.noPrivilegeEditDisabled}
                  arrow
                  placement='top'
                  componentsProps={TOOLTIP_DISABLED_PROPS}
                >
                  <S.EditIcon
                    $isDisabled={true}
                    data-disabled='true'
                    data-edit-icon
                  >
                    <DriveFileRenameOutlineIcon />
                  </S.EditIcon>
                </Tooltip>
              ) : isDisabledByRecordPrivilege ? (
                <Tooltip
                  title={ERRORS.noPrivilegeEditDisabled}
                  arrow
                  placement='top'
                  componentsProps={TOOLTIP_DISABLED_PROPS}
                >
                  <S.EditIcon
                    $isDisabled={true}
                    data-disabled='true'
                    data-edit-icon
                  >
                    <DriveFileRenameOutlineIcon />
                  </S.EditIcon>
                </Tooltip>
              ) : (
                <S.EditIcon
                  data-edit-icon
                  onClick={(e) => {
                    e.stopPropagation();
                    onCellEdit(itemId, key, cellValue);
                  }}
                >
                  <DriveFileRenameOutlineIcon />
                </S.EditIcon>
              ))}
            {isCellLocked && lockedByOther && (
              <Tooltip
                title={lockedByOther.user}
                arrow
                placement='top'
                componentsProps={TOOLTIP_DISABLED_PROPS}
              >
                <S.LockedUserCircle
                  $backgroundColor={getColorForUser(lockedByOther.user)}
                >
                  {formatUserDisplay(lockedByOther.user)}
                </S.LockedUserCircle>
              </Tooltip>
            )}
            {key === 'job' &&
              cellValue &&
              item.jobId &&
              (() => {
                const canAddLoad =
                  canAddAllLoads ||
                  (hasOwnerAddLoadPrivilege &&
                    item.ownerId != null &&
                    ownerIds.has(item.ownerId));
                return canAddLoad ? (
                  <S.AddToJobIcon
                    data-add-to-job-icon
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        `/jobs/${item.jobId}/loads/createContainer`,
                        '_blank',
                      );
                    }}
                  >
                    <AddIcon />
                  </S.AddToJobIcon>
                ) : (
                  <Tooltip
                    title={ERRORS.noPrivilegeAddLoadDisabled}
                    arrow
                    placement='top'
                    componentsProps={TOOLTIP_DISABLED_PROPS}
                  >
                    <S.AddToJobIcon
                      $isDisabled={true}
                      data-add-to-job-icon
                      data-disabled='true'
                    >
                      <AddIcon />
                    </S.AddToJobIcon>
                  </Tooltip>
                );
              })()}
          </S.HoverableStyledTableCell>
        );
      })}
      {isDateChanged && dateChangeInfo && (
        <RowWarningOverlay
          itemId={itemId}
          type={
            dateChangeInfo.status === 'accepted'
              ? 'dateChangeAccepted'
              : 'dateChangePending'
          }
          info={dateChangeInfo}
          disabled={
            dateChangeInfo.status === 'accepted'
              ? false
              : !isRecordAccessible ||
                !(canEditAllColumns || hasTransportEditPrivilege)
          }
          onAction={
            dateChangeInfo.status === 'accepted'
              ? onReload
              : () => onAcceptDateChange(itemId)
          }
        />
      )}
      {isDeleted && <RowWarningOverlay type='deleted' onAction={onReload} />}
    </S.StyledTableRow>
  );
};

const dateChangeTooltipSx = {
  bgcolor: '#ff9800',
  maxWidth: '480px',
  fontSize: '0.7rem',
  '& .MuiTooltip-arrow': { color: '#ff9800' },
} as const;

const dateChangeAcceptedTooltipSx = {
  bgcolor: '#ffb74d',
  maxWidth: '480px',
  fontSize: '0.7rem',
  '& .MuiTooltip-arrow': { color: '#ffb74d' },
} as const;

const deletedTooltipSx = {
  bgcolor: '#8e8e8e',
  maxWidth: '480px',
  fontSize: '0.7rem',
  '& .MuiTooltip-arrow': { color: '#8e8e8e' },
} as const;

const reloadButtonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#022673',
  color: '#fff',
  padding: '0 8px',
  borderRadius: '3px',
  minHeight: '20px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '0.6rem',
  transition: 'background-color 0.2s',
  '&:hover': { backgroundColor: '#022e8d' },
} as const;

const reloadButtonDisabledSx = {
  ...reloadButtonSx,
  backgroundColor: '#8e8e8e',
  cursor: 'not-allowed',
  '&:hover': { backgroundColor: '#8e8e8e' },
} as const;

const overlayFlexSx = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 2,
} as const;

const overlayTextSx = {
  flex: 1,
} as const;

const overlayTextCenterSx = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
} as const;

const RowWarningOverlay: React.FC<{
  itemId?: string;
  type: 'dateChangePending' | 'dateChangeAccepted' | 'deleted';
  info?: { newDate: string };
  disabled?: boolean;
  onAction: () => void;
}> = React.memo(({ itemId, type, info, disabled, onAction }) => {
  const isDateChange = type !== 'deleted';
  const tooltipText = info
    ? type === 'dateChangeAccepted'
      ? OVERLAYS.dateChangeAcceptedOverlay(info.newDate)
      : type === 'dateChangePending'
      ? OVERLAYS.dateChangeOverlay(info.newDate)
      : OVERLAYS.deleteOverlay
    : OVERLAYS.deleteOverlay;
  const tooltipSx =
    type === 'dateChangeAccepted'
      ? dateChangeAcceptedTooltipSx
      : type === 'dateChangePending'
      ? dateChangeTooltipSx
      : deletedTooltipSx;
  const bgColor =
    type === 'dateChangeAccepted'
      ? COLORS.dateChangedAcceptedWarning
      : type === 'dateChangePending'
      ? COLORS.dateChangedWarning
      : COLORS.darkGray;
  const hoverBgColor =
    type === 'dateChangeAccepted'
      ? COLORS.dateChangedAcceptedWarningHover
      : type === 'dateChangePending'
      ? COLORS.dateChangedWarningHover
      : '#212121';
  const actionLabel =
    type === 'dateChangePending' ? BUTTONS.accept : BUTTONS.reload;

  return (
    <S.WarningIconWrapper>
      <Tooltip
        title={
          <Box sx={overlayFlexSx}>
            <Box sx={isDateChange ? overlayTextSx : overlayTextCenterSx}>
              {tooltipText}
            </Box>
            <Box
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                if (disabled) return;
                onAction();
              }}
              sx={disabled ? reloadButtonDisabledSx : reloadButtonSx}
            >
              {actionLabel}
            </Box>
          </Box>
        }
        arrow
        placement='left'
        componentsProps={{
          tooltip: { sx: tooltipSx },
        }}
      >
        <S.WarningIcon
          $bgColor={bgColor}
          $hoverBgColor={hoverBgColor}
          data-item-id={itemId}
          data-warning-icon={isDateChange ? 'dateChange' : undefined}
        >
          {isDateChange ? <WarningAmberIcon /> : <DeleteIcon />}
        </S.WarningIcon>
      </Tooltip>
    </S.WarningIconWrapper>
  );
});

RowWarningOverlay.displayName = 'RowWarningOverlay';

export const TableRowItem = React.memo(TableRowItemInner);
